import {useState} from 'react';

import {useDeleteZoneMutation} from './useZoneMutations';
import {useZonesSelection} from './useZonesSelection';
import {DnsZone} from '../types';

// FNP-1191 CRUD half — WHAT HAPPENS BETWEEN CLICKING DELETE AND THE ZONE BEING
// GONE. Owns the modal's open state, the zone under consideration, and the
// mutation, so neither the toolbar nor the modal has to.
//
// ⚠️ ONE ZONE AT A TIME, DELIBERATELY, EVEN THOUGH SELECTION IS MULTI-ROW.
//
// The DELETE pattern doc's `deleteRecords(ids)` fans out over a selection with
// `Promise.all`. That is wrong for this operation and the reason is specific:
//
//   1. EVERY DELETE HAS ITS OWN REFUSAL. A batch of five where the third is
//      ZONE_NOT_EMPTY and the fourth is PERMISSION_DENIED produces a partial
//      apply with two different reasons, and `Promise.all` rejects on the first
//      one — losing the rest. The tenant is then told one thing about five
//      zones, two of which are already gone.
//   2. THE GUARD IS PER-ZONE. Delegation is a property of the individual zone,
//      so a batch would need a per-zone verdict rendered in one modal, which is
//      a list of warnings nobody reads before clicking the red button.
//   3. THE DAMAGE IS UNRECOVERABLE. Flexi keeps no copy to restore from.
//
// So the toolbar refuses a multi-row delete rather than pretending to do one.
// Selection stays multi-row because the table's checkbox column is standard and
// removing it for this module alone would be its own surprise.

export const useZoneDeletion = () => {

    const [zone, setZone] = useState<DnsZone | undefined>(undefined);
    const [opened, setOpened] = useState(false);

    const deleteMutation = useDeleteZoneMutation();
    const {clearSelection} = useZonesSelection();

    // Opening RESETS the previous failure. Without this, a modal reopened after
    // a ZONE_NOT_EMPTY refusal still shows that refusal against a different
    // zone — a stale claim about a zone that may well be empty.
    const requestDelete = (target: DnsZone) => {
        deleteMutation.reset();
        setZone(target);
        setOpened(true);
    };

    const cancelDelete = () => {
        setOpened(false);
        // ⚠️ The zone is NOT cleared here. Mantine's Drawer/Modal animate out,
        // and clearing it synchronously would blank the modal's contents for
        // the length of that transition — the operator watches the zone name
        // they were deciding about vanish as it closes.
    };

    const confirmDelete = async () => {
        if (!zone) {
            return;
        }
        try {
            await deleteMutation.mutateAsync({
                connectionId: zone.connectionId,
                id: zone.id,
            });
            // ⚠️ ONLY ON SUCCESS. A refused delete keeps the modal OPEN so the
            // ZONE_NOT_EMPTY explanation stays in front of the operator — the
            // one place they can read what to do next. Closing on failure would
            // leave a toast as the only record of a refusal that names a
            // concrete remedy.
            setOpened(false);
            clearSelection();
        } catch {
            // Classified and notified by the mutation's onError, and rendered
            // inline by the modal off `deleteMutation.error`. Swallowed here so
            // a rejection the user has already been told about does not surface
            // as an unhandled promise rejection.
        }
    };

    return {
        zone,
        opened,
        requestDelete,
        cancelDelete,
        confirmDelete,
        isDeleting: deleteMutation.isPending,
        // The RAW rejection. The modal classifies it — one classifier, one call
        // site, so the modal and the toast cannot disagree about what a failure
        // was.
        error: deleteMutation.error,
    };
};
